// The non-resource endpoints the console shell calls (resources.md §2.2, ADR-081). Shapes follow the ADRs; the
// generated OpenAPI types replace these interfaces once the A1 and F3 documents are published.
import type { NodusClient } from "./client";
import { newIdempotencyKey } from "./client";
import { collectionPath, endpoints } from "./paths";
import type { KList, KObject } from "./types";

/**
 * GET /auth/whoami as identity serves it: the credential's kind, the user's uid, the org's uid and name, the role,
 * the project restriction (empty means every project) and the concrete scopes held.
 */
export interface WhoAmI {
  kind?: string;
  user?: string;
  org?: string;
  orgName?: string;
  role?: string;
  projects?: string[];
  scopes?: string[];
  aal?: string;
}

export interface PendingInvite {
  uid: string;
  org: string;
  orgDisplayName: string;
  role: string;
  inviter?: string;
  email?: string;
  projects?: string[];
  expirationTime: string;
}

/** An org that verified the domain of the caller's verified email, which they may join as a Member. */
export interface JoinableOrg {
  name: string;
  displayName: string;
  domain: string;
}

export type Organization = KObject<{ displayName?: string }, { phase?: string; role?: string }>;
export type Project = KObject<{ displayName?: string; description?: string }>;

export interface CliKeysRequest {
  orgs: string[];
  codeChallenge: string;
  state: string;
  name: string;
}

export interface RunOutcome {
  kind: string;
  phase: string;
  day: string;
  count: number;
}

/** A pending MCP client authorization (ADR-002, ADR-082); the console is the only writer of OAuthGrant. */
export interface AgentAuthorization {
  id: string;
  client: { name: string; redirectHost: string; verified: boolean; logoURL?: string };
  requestedScopes: string[];
  expirationTime?: string;
}

export interface AgentAuthorizationDecision {
  decision: "approve" | "deny";
  org?: string;
  /** Omitted for every project in the org. */
  project?: string;
  scopes?: string[];
  expiresInDays?: number;
}

export function shellApi(client: NodusClient) {
  return {
    whoami: () => client.get<WhoAmI>(endpoints.whoami),
    invites: () => client.get<{ items: PendingInvite[] }>(endpoints.invites).then((r) => r.items ?? []),
    acceptInvite: (by: { invite: string } | { token: string }) =>
      client.post<{ org: string }>(endpoints.invitesAccept, by, { idempotencyKey: newIdempotencyKey() }).then((r) => r.data),
    lookupInvite: (token: string) => client.post<PendingInvite>(endpoints.invitesLookup, { token }).then((r) => r.data),
    joinableOrgs: () => client.get<{ items: JoinableOrg[] }>(endpoints.joinableOrgs).then((r) => r.items ?? []),
    joinOrg: (org: string) => client.post<{ org: string }>(endpoints.joinOrg, { org }, { idempotencyKey: newIdempotencyKey() }).then((r) => r.data),
    /** The orgs the caller belongs to: Organization is one object per org, listed without a current org. */
    organizations: () => client.get<KList<Organization>>(collectionPath("Organization")).then((r) => r.items),
    projects: (org: string) => client.get<KList<Project>>(collectionPath("Project"), { org }).then((r) => r.items),
    /** The explicit create-org step (ADR-081): the one org-scoped create that needs no current org. */
    createOrganization: (name: string, displayName: string, idempotencyKey: string) =>
      client
        .post<Organization>(
          collectionPath("Organization"),
          { apiVersion: "nodus.dev/v1", kind: "Organization", metadata: { name }, spec: { displayName } },
          { idempotencyKey },
        )
        .then((r) => r.data),
    signupCard: (returnURL: string, restart = false) => client.post<{ url: string }>("/auth/signup-card", { returnURL, restart }).then((r) => r.data),
    createCliKeys: (req: CliKeysRequest) =>
      client.post<{ code: string }>(endpoints.cliKeys, req, { idempotencyKey: newIdempotencyKey() }).then((r) => r.data),
    approveDevice: (userCode: string, orgs: string[]) =>
      client.post<unknown>(endpoints.deviceApprove, { user_code: userCode, orgs }).then((r) => r.data),
    denyDevice: (userCode: string) => client.post<unknown>(endpoints.deviceDeny, { user_code: userCode }).then((r) => r.data),
    runOutcomes: (org: string, project: string, since: "7d" | "30d" | "90d") =>
      client
        .get<{ items: RunOutcome[] }>(endpoints.runOutcomes(project), { org, query: { groupBy: "kind,phase,day", since } })
        .then((r) => r.items ?? []),
    agentAuthorization: (id: string) => client.get<AgentAuthorization>(endpoints.agentAuthorization(id)),
    decideAgentAuthorization: (id: string, d: AgentAuthorizationDecision) =>
      client.post<{ redirectTo?: string }>(endpoints.agentAuthorization(id), d, { idempotencyKey: newIdempotencyKey() }).then((r) => r.data),
    getSettings: () => client.get<Record<string, unknown>>(endpoints.meSettings),
    putSettings: (settings: Record<string, unknown>) => client.put<Record<string, unknown>>(endpoints.meSettings, settings).then((r) => r.data),
  };
}

export type ShellApi = ReturnType<typeof shellApi>;

/** Reserved org slugs: top-level console segments and reserved words (ui-spec §2.8). */
export const RESERVED_ORG_SLUGS: ReadonlySet<string> = new Set([
  "account",
  "auth",
  "sign-in",
  "sign-up",
  "forgot-password",
  "reset-password",
  "verify-email",
  "oauth",
  "agent-authorize",
  "invite",
  "onboarding",
  "marketplace",
  "reserved-compute",
  "cli-auth",
  "moved",
  "console",
  "sandboxes",
  "api",
  "docs",
  "admin",
  "new",
  "static",
]);

/** DNS-1123 label and not reserved; returns the problem in plain words, or undefined when valid. */
export function orgSlugProblem(slug: string): string | undefined {
  if (!slug) return "Enter a URL name.";
  if (slug.length > 63) return "Use at most 63 characters.";
  if (!/^[a-z0-9]([-a-z0-9]*[a-z0-9])?$/.test(slug)) return "Use lowercase letters, digits and hyphens; start and end with a letter or digit.";
  if (RESERVED_ORG_SLUGS.has(slug)) return `"${slug}" is reserved. Choose another name.`;
  return undefined;
}

/** "Research team" → "research-team". */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 63)
    .replace(/-+$/g, "");
}
